import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import tesoreriaCajaService, {
    ImportacionMedioCobroRenglon,
    ImportacionMediosCobroFilaMensaje,
    ImportacionMediosCobroResult,
} from '../../services/tesoreriaCajaService';
import { Imputable } from '../../services/imputableService';
import { GrupoCajaChica } from '../../services/tesoreriaService';
import { downloadBlob } from '../../utils/downloadBlob';
import { formatCurrency } from '../../utils/formatters';
import { resolverNroSucursalCheque } from '../../utils/chequeTerceroEstado';
import { useAuth } from '../../contexts/AuthContext';
import { effectiveUsername } from '../../utils/sistemaAuthz';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import SearchableSelect from '../SearchableSelect';
import GrupoCajaSelect from './GrupoCajaSelect';
import ImputableSearch from '../ImputableSearch';
import ImputableInlineSearchField from './ImputableInlineSearchField';

interface ImportarMediosCobroDialogProps {
    open: boolean;
    onClose: () => void;
    /** cargar: deja los renglones en el ingreso abierto. registrar: graba el ingreso vario. */
    modo?: 'cargar' | 'registrar';
    onCargado?: (renglones: ImportacionMedioCobroRenglon[]) => void;
    onRegistrado?: () => void;
}

const mediosDesdeRenglones = (renglones: ImportacionMedioCobroRenglon[]) =>
    renglones.map((r) => {
        const base: Record<string, unknown> = {
            mcp: r.mcp || '',
            importe: Number(r.importe) || 0,
            detalle: r.observaciones || '',
        };
        if (r.tipoPago === 'Cheque') {
            const esElectronico = Boolean(r.esElectronico);
            base.cheque = {
                nroBanco: r.nroBanco ?? 0,
                nroSucursal: resolverNroSucursalCheque(esElectronico, r.nroSucursal) ?? 0,
                nroCheque: r.nroCheque || '',
                nroLibrador: r.cuitLibrador || '',
                librador: r.librador || '',
                fechaEmision: r.fechaEmision ? new Date(r.fechaEmision).toISOString() : new Date().toISOString(),
                fechaVencimiento: r.fechaVencimiento ? new Date(r.fechaVencimiento).toISOString() : new Date().toISOString(),
                ctaBancaria: r.ctaBancaria || '',
                concepto: (r.observaciones || r.concepto || '').trim().slice(0, 30),
                esElectronico,
            };
        }
        return base;
    });

const fechaLocalIso = (isoDate: string) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0).toISOString();
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const extraerMensajeError = (error: any, porDefecto: string): string => {
    const data = error?.response?.data;
    if (!data) return porDefecto;
    if (typeof data === 'string') return data;
    if (data.errors && typeof data.errors === 'object') {
        const primera = Object.keys(data.errors)[0];
        const msg = (data.errors as Record<string, string[]>)[primera]?.[0];
        if (msg) return `${data.message ? `${data.message} ` : ''}${msg}`;
    }
    return data.message || data.title || porDefecto;
};

const TablaMensajes: React.FC<{ titulo: string; items: ImportacionMediosCobroFilaMensaje[]; color: 'error' | 'warning' }> = ({
    titulo,
    items,
    color,
}) => (
    <Box>
        <Typography variant="subtitle2" color={`${color}.main`} sx={{ mb: 0.5 }}>
            {titulo} ({items.length})
        </Typography>
        <TableContainer sx={{ maxHeight: 220, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 70 }}>Fila</TableCell>
                        <TableCell sx={{ width: 140 }}>Columna</TableCell>
                        <TableCell>Mensaje</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((e, i) => (
                        <TableRow key={`${e.fila}-${e.columna}-${i}`}>
                            <TableCell>{e.fila > 0 ? e.fila : 'General'}</TableCell>
                            <TableCell>{e.columna || '-'}</TableCell>
                            <TableCell>{e.mensaje}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const ImportarMediosCobroDialog: React.FC<ImportarMediosCobroDialogProps> = ({
    open,
    onClose,
    modo = 'cargar',
    onCargado,
    onRegistrado,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { sucursales, loading: loadingSucursales, preferidaNro } = useSucursalesUsuarioContext();
    const usuario = effectiveUsername(user?.username) || 'Sistema';
    const registrarDirecto = modo === 'registrar';
    const inputId = `excel-upload-saldos-caja-${modo}`;
    const [archivo, setArchivo] = useState<File | null>(null);
    const [omitirDuplicados, setOmitirDuplicados] = useState(false);
    const [resultado, setResultado] = useState<ImportacionMediosCobroResult | null>(null);
    const [validando, setValidando] = useState(false);
    const [registrando, setRegistrando] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
    const [nroSuc, setNroSuc] = useState(0);
    const [imputable, setImputable] = useState<Imputable | null>(null);
    const [openImputable, setOpenImputable] = useState(false);
    const [grupo, setGrupo] = useState<GrupoCajaChica | null>(null);
    const [grupoKey, setGrupoKey] = useState(0);
    const [configAnchor, setConfigAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) return;
        setArchivo(null);
        setResultado(null);
        setOmitirDuplicados(false);
        setFecha(new Date().toISOString().slice(0, 10));
        setNroSuc(preferidaNro && preferidaNro > 0 ? preferidaNro : 0);
        setImputable(null);
        setGrupo(null);
        setGrupoKey((k) => k + 1);
        setConfigAnchor(null);
    }, [open, preferidaNro]);

    const validacionOk = !!resultado && (resultado.errores?.length ?? 0) === 0 && (resultado.renglones?.length ?? 0) > 0;
    const ocupado = validando || registrando || descargando;

    const sucursalLabel = useMemo(() => {
        if (!nroSuc || nroSuc <= 0) return null;
        const sucursal = sucursales.find((s) => s.nroSuc === nroSuc);
        const nombre = sucursal?.nombreSucursal?.trim();
        return nombre
            ? `${String(nroSuc).padStart(5, '0')} — ${nombre}`
            : String(nroSuc).padStart(5, '0');
    }, [nroSuc, sucursales]);

    const configResumen = useMemo(() => {
        if (!registrarDirecto) {
            return omitirDuplicados ? 'Omitir cheques duplicados' : null;
        }
        const partes: string[] = [];
        if (sucursalLabel) partes.push(`sucursal ${sucursalLabel}`);
        if (fecha) partes.push(`fecha ${fecha}`);
        if (imputable) partes.push(`concepto ${imputable.nomCuenta || `#${imputable.nroCuenta}`}`);
        if (grupo?.codigo) partes.push(`caja ${grupo.nombre || grupo.codigo}`);
        if (omitirDuplicados) partes.push('omitir duplicados');
        return partes.length ? partes.join(' · ') : null;
    }, [registrarDirecto, sucursalLabel, fecha, imputable, grupo, omitirDuplicados]);

    const handleClose = () => {
        if (ocupado) return;
        setConfigAnchor(null);
        onClose();
    };

    const handleDescargarPlantilla = async () => {
        try {
            setDescargando(true);
            const blob = await tesoreriaCajaService.descargarPlantillaImportacionMediosCobro();
            downloadBlob(blob, 'Plantilla_Importacion_SaldosCaja.xlsx', XLSX_MIME);
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'No se pudo descargar la plantilla'), { variant: 'error' });
        } finally {
            setDescargando(false);
        }
    };

    const handleArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (file && !/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
            enqueueSnackbar('Solo se admiten archivos .xlsx, .xlsm o .xls', { variant: 'warning' });
            return;
        }
        setArchivo(file);
        setResultado(null);
    };

    const validar = async () => {
        if (!archivo) {
            enqueueSnackbar('Seleccioná un archivo Excel', { variant: 'warning' });
            return null;
        }
        try {
            setValidando(true);
            const res = await tesoreriaCajaService.importarMediosCobro({
                archivo,
                omitirDuplicados,
            });
            setResultado(res);
            if ((res.errores?.length ?? 0) > 0) {
                enqueueSnackbar(`El archivo tiene ${res.errores.length} error(es). Corregilos y volvé a validar.`, {
                    variant: 'warning',
                });
            }
            return res;
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'Error al validar el archivo'), {
                variant: 'error',
                autoHideDuration: 8000,
            });
            return null;
        } finally {
            setValidando(false);
        }
    };

    const cargar = async () => {
        const res = validacionOk && resultado ? resultado : await validar();
        if (!res || (res.errores?.length ?? 0) > 0 || (res.renglones?.length ?? 0) === 0) return;
        onCargado?.(res.renglones);
    };

    const registrar = async () => {
        if (!nroSuc || nroSuc <= 0) {
            enqueueSnackbar('Seleccioná la sucursal del ingreso', { variant: 'warning' });
            return;
        }
        if (!imputable) {
            enqueueSnackbar('Seleccioná el concepto del ingreso', { variant: 'warning' });
            return;
        }
        if (!grupo?.codigo) {
            enqueueSnackbar('Seleccioná el grupo de caja', { variant: 'warning' });
            return;
        }
        const res = validacionOk && resultado ? resultado : await validar();
        if (!res || (res.errores?.length ?? 0) > 0 || (res.renglones?.length ?? 0) === 0) return;
        try {
            setRegistrando(true);
            await api.post('/api/TesoreriaCaja/movimientos-caja', {
                fecha: fechaLocalIso(fecha),
                grupoMCP: grupo.codigo,
                nroSuc,
                tipoMovimiento: '5',
                codigoEntidad: imputable.nroCuenta,
                descripcion: imputable.nomCuenta || '',
                aclaracion: '',
                mediosDeCobro: mediosDesdeRenglones(res.renglones),
            });
            enqueueSnackbar('Ingreso registrado', { variant: 'success' });
            onRegistrado?.();
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'No se pudo registrar el ingreso'), {
                variant: 'error',
                autoHideDuration: 8000,
            });
        } finally {
            setRegistrando(false);
        }
    };

    return (
        <Dialog open={open} onClose={ocupado ? undefined : handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30 }} />
                    <Typography variant="h6" fontWeight={700}>
                        Importar saldos
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                        <Typography component="span" fontWeight={700} fontSize={18}>
                            1. Descargar Plantilla
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Descargá la plantilla Excel con el formato correcto para importar saldos de caja (medios MCP,
                        cheques, efectivo, transferencias).
                    </Typography>
                    <Button
                        variant="contained"
                        color="info"
                        fullWidth
                        sx={{ fontWeight: 700, py: 1.2 }}
                        startIcon={descargando ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon />}
                        onClick={() => void handleDescargarPlantilla()}
                        disabled={descargando}
                    >
                        Descargar Plantilla
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                            <Typography component="span" fontWeight={700} fontSize={18}>
                                2. Importar Plantilla
                            </Typography>
                        </Box>
                        <Tooltip title={registrarDirecto ? 'Datos del ingreso (sucursal, fecha, concepto, caja)' : 'Opciones de importación'}>
                            <IconButton
                                size="small"
                                color="primary"
                                aria-label="Configurar importación"
                                onClick={(e) => setConfigAnchor(e.currentTarget)}
                                disabled={ocupado}
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Seleccioná el Excel. Columnas: MCP, Importe, Observaciones y, si es cheque: NroBanco,
                        NroCheque, CuitLibrador, Librador, FechaVencimiento (opcionales: NroSucursal, Electronico,
                        etc.).
                    </Typography>
                    {configResumen && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {configResumen}
                        </Typography>
                    )}
                    {!configResumen && <Box sx={{ mb: 2 }} />}

                    <Popover
                        open={Boolean(configAnchor)}
                        anchorEl={configAnchor}
                        onClose={() => setConfigAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { p: 2, width: 400, maxWidth: '90vw' } } }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                            {registrarDirecto ? 'Datos del ingreso' : 'Opciones'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {registrarDirecto
                                ? 'Se registra el ingreso vario con los renglones del archivo, sin pasar por la carga manual.'
                                : 'Cada fila queda en el ingreso vario como si la hubieras cargado a mano.'}
                        </Typography>
                        <Stack spacing={1.5}>
                            {registrarDirecto && (
                                <>
                                    <SearchableSelect
                                        label="Sucursal"
                                        size="small"
                                        required
                                        loading={loadingSucursales}
                                        options={sucursales.map((sucursal) => ({
                                            value: sucursal.nroSuc,
                                            label: sucursal.nombreSucursal?.trim()
                                                ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                                : `Suc. ${sucursal.nroSuc}`,
                                        }))}
                                        value={nroSuc > 0 ? nroSuc : null}
                                        onChange={(value) => setNroSuc(Number(value) || 0)}
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Fecha de emisión"
                                        type="date"
                                        value={fecha}
                                        onChange={(e) => setFecha(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        disabled={ocupado}
                                    />
                                    {!imputable ? (
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <ImputableInlineSearchField
                                                    label="Concepto imputable"
                                                    placeholder="Número de cuenta o nombre"
                                                    tipoMovimientoIE="I"
                                                    onSelect={setImputable}
                                                    onOpenModal={() => setOpenImputable(true)}
                                                />
                                            </Box>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                onClick={() => setOpenImputable(true)}
                                                disabled={ocupado}
                                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25 }}
                                            >
                                                Seleccionar
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="flex-start"
                                                    justifyContent="space-between"
                                                    spacing={1}
                                                >
                                                    <Box minWidth={0}>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            {imputable.nomCuenta || 'Sin nombre'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Cuenta #{imputable.nroCuenta}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row">
                                                        <Tooltip title="Cambiar concepto">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setOpenImputable(true)}
                                                                disabled={ocupado}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Quitar concepto">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => setImputable(null)}
                                                                disabled={ocupado}
                                                            >
                                                                <DeleteOutline fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    )}
                                    <GrupoCajaSelect
                                        key={grupoKey}
                                        usuario={usuario}
                                        value={grupo}
                                        onChange={setGrupo}
                                        required
                                    />
                                </>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={omitirDuplicados}
                                        onChange={(e) => {
                                            setOmitirDuplicados(e.target.checked);
                                            setResultado(null);
                                        }}
                                        disabled={ocupado}
                                    />
                                }
                                label="Omitir cheques ya cargados en lugar de rechazar el archivo"
                            />
                        </Stack>
                    </Popover>

                    <label htmlFor={inputId}>
                        <input
                            id={inputId}
                            type="file"
                            accept=".xlsx,.xlsm,.xls"
                            style={{ display: 'none' }}
                            onChange={handleArchivo}
                            disabled={ocupado}
                        />
                        <Button
                            variant="contained"
                            fullWidth
                            color="success"
                            startIcon={<CloudUploadIcon />}
                            component="span"
                            disabled={ocupado}
                            sx={{ fontWeight: 700, py: 1.2 }}
                        >
                            Seleccionar Archivo
                        </Button>
                    </label>

                    {archivo && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {archivo.name}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={ocupado}
                                onClick={() => void validar()}
                                startIcon={validando ? <CircularProgress size={16} /> : undefined}
                                sx={{ fontWeight: 700 }}
                            >
                                Validar y ver previa
                            </Button>
                        </Box>
                    )}

                    {resultado && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                Vista previa
                            </Typography>
                            <Alert
                                severity={(resultado.errores?.length ?? 0) > 0 ? 'error' : 'info'}
                                sx={{ mb: 2 }}
                            >
                                {(resultado.errores?.length ?? 0) > 0
                                    ? 'El archivo tiene errores. No se pueden cargar los renglones hasta corregirlos.'
                                    : registrarDirecto
                                      ? 'Lectura correcta. Confirmá para registrar el ingreso.'
                                      : 'Lectura correcta. Revisá los renglones y cargalos en el ingreso vario.'}
                            </Alert>

                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: '#e3f2fd',
                                    border: '1px solid #bbdefb',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Filas leídas:</strong> {resultado.filasLeidas} ·{' '}
                                    <strong>Válidas:</strong> {resultado.filasValidas}
                                    {(resultado.filasConError ?? 0) > 0 && (
                                        <>
                                            {' '}
                                            · <strong>Con error:</strong> {resultado.filasConError}
                                        </>
                                    )}
                                    {(resultado.filasOmitidas ?? 0) > 0 && (
                                        <>
                                            {' '}
                                            · <strong>Omitidas:</strong> {resultado.filasOmitidas}
                                        </>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Cheques:</strong> {resultado.cantidadCheques} ·{' '}
                                    <strong>Total:</strong> {formatCurrency(resultado.importeTotal)}
                                </Typography>
                            </Box>

                            {(resultado.renglones?.length ?? 0) > 0 && (
                                <TableContainer
                                    sx={{ maxHeight: 240, mb: 2, border: '1px solid #eee', borderRadius: 1 }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tipo</TableCell>
                                                <TableCell>MCP</TableCell>
                                                <TableCell>Detalle</TableCell>
                                                <TableCell>Observaciones</TableCell>
                                                <TableCell align="right">Importe</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {resultado.renglones.map((r) => (
                                                <TableRow key={r.fila}>
                                                    <TableCell>{r.tipoPago}</TableCell>
                                                    <TableCell>{r.mcp}</TableCell>
                                                    <TableCell>
                                                        {r.tipoPago === 'Cheque'
                                                            ? [r.nombreBanco, r.nroCheque ? `Nº ${r.nroCheque}` : '']
                                                                  .filter(Boolean)
                                                                  .join(' · ')
                                                            : r.descripcionMcp || ''}
                                                    </TableCell>
                                                    <TableCell>{r.observaciones || '-'}</TableCell>
                                                    <TableCell align="right">{formatCurrency(r.importe)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {(resultado.errores?.length ?? 0) > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <TablaMensajes titulo="Errores" items={resultado.errores} color="error" />
                                </Box>
                            )}
                            {(resultado.advertencias?.length ?? 0) > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <TablaMensajes titulo="Advertencias" items={resultado.advertencias} color="warning" />
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!archivo || ocupado || (!!resultado && !validacionOk)}
                                onClick={() => void (registrarDirecto ? registrar() : cargar())}
                                startIcon={
                                    ocupado ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <CloudUploadIcon />
                                    )
                                }
                                sx={{ fontWeight: 700 }}
                            >
                                {registrarDirecto
                                    ? validacionOk
                                        ? `Registrar ingreso (${resultado.renglones.length} saldos)`
                                        : 'Registrar ingreso'
                                    : validacionOk
                                      ? `Cargar ${resultado.renglones.length} saldos`
                                      : 'Cargar en el ingreso'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={ocupado}>
                    Cerrar
                </Button>
            </DialogActions>
            {registrarDirecto && (
                <ImputableSearch
                    open={openImputable}
                    onClose={() => setOpenImputable(false)}
                    onSelect={(item) => {
                        setImputable(item);
                        setOpenImputable(false);
                    }}
                    tipoMovimientoIE="I"
                />
            )}
        </Dialog>
    );
};

export default ImportarMediosCobroDialog;
